export default function DataSecurity() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 md:py-24 text-foreground">
      <header className="mb-12 border-b border-border pb-8">
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-3">
          Data Security
        </h1>
        <p className="text-base text-muted-foreground">
          Last updated May 4, 2026
        </p>
      </header>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Our approach
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            LegacyLens is built around a simple commitment: the memories
            families entrust to us must outlast us. That commitment requires
            more than software design — it requires infrastructure that
            protects data against accidental deletion, malicious tampering,
            and operational failure.
          </p>
          <p>
            This page describes how we protect the data you place in our
            care, in plain language, with no aspirational claims. Everything
            below reflects the security posture in production today.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          How we protect data
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            <strong>In transit.</strong> All traffic between your device and
            LegacyLens services is encrypted using HTTPS (TLS 1.2 or higher).
            Our domains are protected by Cloudflare, which terminates TLS at
            the network edge.
          </p>
          <p>
            <strong>At rest.</strong> Every byte of customer data is encrypted
            at rest:
          </p>
        </div>
        <ul className="mt-5 space-y-2 text-lg leading-relaxed list-disc pl-6">
          <li>
            Tribute artifacts (3D models, voice recordings, audio, photos) and
            consent records are stored in Cloudflare R2, which encrypts all
            objects at rest using AES-256 with Cloudflare-managed keys.
          </li>
          <li>
            Backup copies are stored in Amazon S3 with server-side encryption
            using Amazon S3 managed keys (SSE-S3, AES-256).
          </li>
          <li>
            Database content is stored in Supabase, which encrypts data at
            rest by platform default.
          </li>
        </ul>
        <div className="mt-5 space-y-5 text-lg leading-relaxed">
          <p>
            We do not allow customer-provided encryption keys (SSE-C is
            blocked at the bucket level) to prevent misconfigurations that
            could leave data in an inconsistent encryption state.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Where data lives
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>LegacyLens hosts all user data in the United States:</p>
        </div>
        <ul className="mt-5 space-y-2 text-lg leading-relaxed list-disc pl-6">
          <li>Cloudflare R2 (primary object storage)</li>
          <li>Amazon S3 — US East (N. Virginia) region (backup storage)</li>
          <li>Supabase — US region (application database)</li>
        </ul>
        <div className="mt-5 space-y-5 text-lg leading-relaxed">
          <p>
            We do not transfer user data outside the United States. We do not
            use any third-party processor located outside the US for tribute
            artifacts, consent records, or database content.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Tribute and consent record durability
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Tribute artifacts and consent records are protected by two
            independent layers of write-once-read-many (WORM) protection:
          </p>
          <p>
            <strong>Primary storage (Cloudflare R2):</strong> Tribute
            artifacts and consent records are protected by R2 Bucket Lock
            Rules with indefinite retention. Once written, locked objects
            cannot be overwritten or deleted through normal operations.
          </p>
          <p>
            <strong>Backup storage (Amazon S3):</strong> Every locked object
            is also mirrored to Amazon S3 with Object Lock enabled in
            Governance mode for a 10-year retention period. Once written to
            S3, these objects cannot be deleted or modified through routine
            operations, accidental deletions, compromised credentials, or
            bucket misconfiguration.
          </p>
          <p>
            This two-layer architecture means a tribute commissioned today is
            protected against:
          </p>
        </div>
        <ul className="mt-5 space-y-2 text-lg leading-relaxed list-disc pl-6">
          <li>Accidental deletion by operators</li>
          <li>Malicious deletion by compromised credentials</li>
          <li>Bucket-level misconfiguration</li>
          <li>Single-platform failures (R2 or S3 outage)</li>
          <li>
            Routine ransomware attack vectors that depend on object deletion
          </li>
        </ul>
        <div className="mt-5 space-y-5 text-lg leading-relaxed">
          <p>
            Tribute artifacts and consent records are mirrored from primary
            storage (Cloudflare R2) to backup storage (Amazon S3) by automated
            processes that run hourly. The Supabase database is backed up to
            Amazon S3 by an automated process that runs daily. We monitor
            backup pipeline health on an ongoing basis and verify successful
            completion through automated logging.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Versioning
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            All buckets containing user data have versioning enabled.
            Versioning preserves prior object states even when newer versions
            are written, providing an additional layer of protection against
            accidental modification.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Access controls
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Access to customer data is limited to a single administrative
            principal — the founder — operating under the principle of least
            privilege. We do not currently have additional staff or
            contractors with access to user data. As we grow, we will publish
            access governance details here.
          </p>
          <p>
            We do not sell user data. We do not share user data with
            advertising networks or data brokers. We do not use user data for
            training third-party AI models. The full list of subprocessors
            who handle data on our behalf is published in our Privacy Policy.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Operational monitoring
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            Production errors and security-relevant events are monitored
            through Sentry. We review error logs on an ongoing basis and
            triage anomalies as they appear.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          What we do not yet have
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            In the spirit of honesty, here is what we have not yet
            implemented:
          </p>
        </div>
        <ul className="mt-5 space-y-2 text-lg leading-relaxed list-disc pl-6">
          <li>We are not currently SOC 2 Type II certified.</li>
          <li>We have not commissioned a third-party penetration test.</li>
          <li>We do not yet operate a public bug bounty program.</li>
          <li>
            We are not HIPAA covered (we are not a covered entity; we do not
            store protected health information).
          </li>
        </ul>
        <div className="mt-5 space-y-5 text-lg leading-relaxed">
          <p>
            These items are on our roadmap. We will update this page as each
            is implemented.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Reporting a security issue
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            If you discover a security vulnerability or have concerns about
            how we are handling user data, please contact us at{" "}
            <a
              href="mailto:info@legacylensai.com"
              className="font-medium underline-offset-4 hover:underline"
            >
              info@legacylensai.com
            </a>
            . We commit to acknowledging security reports within 72 hours.
          </p>
          <p>
            If a security incident affects user data we hold, we commit to
            notifying affected users within 72 hours of confirming the
            incident, in line with industry-standard breach notification
            timelines.
          </p>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6">
          Updates
        </h2>
        <div className="space-y-5 text-lg leading-relaxed">
          <p>
            We will update this page as our security posture evolves.
            Material changes will be reflected in the "Last updated" date
            above. The Privacy Policy provides additional detail about what
            categories of data we collect and how we use it.
          </p>
        </div>
      </section>
    </main>
  );
}
